// components/CreatorSubscriberPosts.jsx
import React, { useRef, useState } from "react";
import { Card, CardContent } from "@mui/material";
import { Chip, Typography, Box, Divider, List } from "@mui/material";
import axiosInstance from "../../utils/config/config";
import SingleVideo from "../HomePage/SingleVideo";
import dayjs from "dayjs";

const getPlanRate = (plan) => {
  if (plan === "Growth Plan") return 0.09;
  if (plan === "Basic Plan") return 0.06;
  return 0;
};

const CreatorSubscriberPosts = ({
  creatorSubscribers,
  posts,
  success,
  setSuccess,
  setErrorState,
  fetchTabData,
  isSubscribed,
  currentPlan,
}) => {
  const videoRefs = useRef([]);
  const [currentPlayingIndex, setCurrentPlayingIndex] = useState(null);

  if (!creatorSubscribers || !posts) return null;

  const handlePlayPause = (index) => {
    videoRefs.current.forEach((video, i) => {
      if (video && i !== index) {
        video.pause();
      }
    });

    if (currentPlayingIndex === index) {
      videoRefs.current[index].pause();
      setCurrentPlayingIndex(null);
    } else {
      videoRefs.current[index].play();
      setCurrentPlayingIndex(index);
    }
  };

  const markPostInactive = async (id) => {
    try {
      const resp = await axiosInstance.put(`/post/markPostInactive/${id}`);
      if (resp) {
        setSuccess({ open: true, message: "Success updating post status." });
        fetchTabData();
      }
    } catch (err) {
      console.log(err);
      setErrorState({ error: true, message: "Error updating post status." });
    }
  };

  const markPostActive = async (id) => {
    try {
      const resp = await axiosInstance.put(`/post/markPostActive/${id}`);
      if (resp) {
        setSuccess({ open: true, message: "Success updating post status." });
        fetchTabData();
      }
    } catch (err) {
      console.log(err);
      setErrorState({ error: true, message: "Error updating post status." });
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", mx: "auto", mt: 2 }}>
      {creatorSubscribers.map(
        ({ subscriber, package: pkg, startDate }, idx) => {
          const purchasedPosts = posts
            .filter(({ subscribers }) =>
              subscribers.some((s) => s.subscriber?.id === subscriber.id)
            )
            .map(({ post }) => post);

          const planRate = getPlanRate(currentPlan);
          const monthsActive = dayjs().diff(dayjs(startDate), "month") + 1;
          const pkgPrice = pkg.price || 0;
          const pkgDeduction = planRate * pkgPrice;
          const pkgCreatorShare = pkgPrice - pkgDeduction;

          return (
            <Card key={idx} sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="h6">
                      {subscriber.firstName} {subscriber.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {subscriber.email}
                    </Typography>
                  </Box>
                  <Chip
                    label={pkg.packageName}
                    color="secondary"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="body2" mt={1}>
                  Plan: <strong>{currentPlan}</strong> | OnlyHands Share:{" "}
                  {planRate * 100}%
                </Typography>
                <Typography variant="body2">
                  Package Price: ${pkgPrice.toFixed(2)} | Deduction: $
                  {pkgDeduction.toFixed(2)} | Creator Share: $
                  {pkgCreatorShare.toFixed(2)}
                </Typography>
                <Typography variant="body2" mb={1}>
                  Subscribed for: {monthsActive} month
                  {monthsActive > 1 ? "s" : ""}
                </Typography>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                  Purchased Posts:
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {purchasedPosts.length ? (
                    <List dense>
                      {purchasedPosts.map((post, index) => {
                        const postPrice = post.price || 0;
                        const postDeduction = planRate * postPrice;
                        const postCreatorShare = postPrice - postDeduction;
                        return (
                          <Box key={index} sx={{ mb: 2 }}>
                            <SingleVideo
                              item={post}
                              videoRef={(ref) =>
                                (videoRefs.current[index] = ref)
                              }
                              onPlayPause={() => handlePlayPause(index)}
                              isPlaying={currentPlayingIndex === index}
                              paid={true}
                              markPostActive={markPostActive}
                              markPostInactive={markPostInactive}
                            />
                            <Typography
                              variant="caption"
                              display="block"
                              mt={0.5}
                            >
                              Post Price: ${postPrice.toFixed(2)} | Deduction: $
                              {postDeduction.toFixed(2)} | Creator Share: $
                              {postCreatorShare.toFixed(2)}
                            </Typography>

                            <Divider sx={{ mt: 2 }} />
                          </Box>
                        );
                      })}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      No posts purchased.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        }
      )}
    </Box>
  );
};

export default CreatorSubscriberPosts;
